import { Flex, Typography } from "../../../../../../shared/frontend/ui";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { FormSectionHeading } from "../../components/FormSectionHeading";
import { ThemedModal } from "../../components/ThemedModal";
import { useAuth } from "../auth/useAuth";
import { spacing } from "../design-system";
import { useI18n } from "../i18n/useI18n";
import { formatDateDisplay } from "../units/format";
import { useEffectiveUnits } from "../units/useEffectiveUnits";
import { SUPPORT_EMAIL, SUPPORT_VENDOR_NAME } from "../supportContact";
import { formatAppVersionShort } from "../version";
import { windowHeight } from "../../../../../../shared/frontend/theme/displayZoom";

type ReleaseNoteItem = { version: string; description: string; descriptionEn: string | null; releasedAt: string | null };
type InfoDto = { manufacturerInfo: string | null; supportVendorName?: string | null; supportEmail?: string | null; releaseNotes: ReleaseNoteItem[]; planName?: string | null };

const PRODUCT_NAME = "m1member";
const PRODUCT_HEADING_ID = "info-modal-product";

export function InfoModal({ open, onClose, isDark }: { open: boolean; onClose: () => void; isDark?: boolean }) {
  const base = import.meta.env.BASE_URL;
  const logoFull = isDark ? `${base}images/mqorva-dm-240x58.png` : `${base}images/mqorva-240x58.png`;
  const auth = useAuth();
  const { text, lang } = useI18n();
  const units = useEffectiveUnits();
  // Über den Query-Cache: mehrfaches Öffnen ergibt einen Abruf, Ladezustand und Fehler kommen
  // aus dem Ergebnis statt aus drei nebenherlaufenden Zuständen.
  const infoQuery = useQuery<InfoDto>({
    queryKey: ["info", auth.info?.tenantId],
    queryFn: async () => (await auth.api.get<InfoDto>("/api/v1/status")) ?? { manufacturerInfo: null, releaseNotes: [] },
    enabled: open
  });
  const data = infoQuery.data ?? null;
  // Erstaufbau, nicht `isFetching`: Der Wrapper sperrt den Inhalt, solange dies wahr ist.
  const loading = infoQuery.isPending && open;
  const error = infoQuery.error ? ((infoQuery.error as Error)?.message ?? text("Error loading")) : null;

  const rawReleaseNotes = data?.releaseNotes;
  const releaseNotes = React.useMemo(() => {
    if (!rawReleaseNotes?.length) return [];
    const useEn = lang === "en";
    return rawReleaseNotes.map((note) => {
      const dateStr = formatDateDisplay(note.releasedAt, units);
      const title = dateStr
        ? `${text("Version")} ${note.version} (${dateStr})`
        : `${text("Version")} ${note.version}`;
      const description = useEn
        ? (note.descriptionEn?.trim() || note.description)
        : (note.description || note.descriptionEn?.trim() || "");
      return { key: `${note.version}-${note.releasedAt ?? ""}`, title, description };
    });
  }, [rawReleaseNotes, lang, text, units]);

  const year = new Date().getFullYear();
  const supportVendorName = (data?.supportVendorName ?? SUPPORT_VENDOR_NAME).trim() || SUPPORT_VENDOR_NAME;
  const supportEmail = (data?.supportEmail ?? SUPPORT_EMAIL).trim();
  const copyrightLine = `© ${year} ${supportVendorName}. ${text("All rights reserved")}`;

  return (
    <ThemedModal
      title={null}
      aria-labelledby={PRODUCT_HEADING_ID}
      open={open}
      onCancel={onClose}
      autoFocusFirstField={false}
      contentLoading={loading}
      footer={null}
      width={560}
      destroyOnClose
      styles={{ body: { paddingTop: spacing.xl } }}
    >
      {error && (
        <Typography.Paragraph type="danger">{error}</Typography.Paragraph>
      )}
      {data && (
        <div style={{ maxHeight: windowHeight("70vh"), overflow: "auto" }}>
          <Flex vertical align="center" gap={spacing.md} style={{ marginBottom: spacing.xl, textAlign: "center" }}>
            <img src={logoFull} alt={text("mQorva logo")} style={{ maxWidth: 240, height: "auto", objectFit: "contain" }} />
            <Typography.Title id={PRODUCT_HEADING_ID} level={4} style={{ margin: 0 }}>
              {PRODUCT_NAME}
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0, maxWidth: 420 }}>
              {text("From the first contact to daily access.")}
            </Typography.Paragraph>
            <Typography.Text type="secondary">{formatAppVersionShort()}</Typography.Text>
            <a href="https://www.mqorva.de/produkte/m1member/#m1member" target="_blank" rel="noopener noreferrer">{text("Desktop downloads")}</a>
            <Typography.Text>{text("Current plan")}: {data.planName?.trim() || text("No plan booked")}</Typography.Text>
          </Flex>
          {(data.manufacturerInfo ?? "").trim() ? (
            <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: spacing.contentGap }}>
              {data.manufacturerInfo}
            </Typography.Paragraph>
          ) : null}
          {releaseNotes.length > 0 ? (
            <FormSectionHeading title={text("What's new")} first={!(data.manufacturerInfo ?? "").trim()}>
              <Flex vertical gap={spacing.md}>
                {releaseNotes.map((note) => (
                  <div key={note.key}>
                    <Typography.Text strong>{note.title}</Typography.Text>
                    {note.description ? (
                      <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginTop: spacing.xs, marginBottom: 0 }}>
                        {note.description}
                      </Typography.Paragraph>
                    ) : null}
                  </div>
                ))}
              </Flex>
            </FormSectionHeading>
          ) : null}
          <div style={{ marginTop: spacing.lg, textAlign: "center" }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: spacing.xs }}>
              {copyrightLine}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {supportEmail ? <a href={`mailto:${supportEmail}`}>{supportEmail}</a> : supportVendorName}
            </Typography.Paragraph>
          </div>
        </div>
      )}
    </ThemedModal>
  );
}
